import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

interface PostViewNavButtonProps {
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  children: ReactNode;
  longPressDurationMs?: number;
  onLongPress?: () => void;
  rotate?: 0 | 90 | 180 | 270;
}

export default function PostViewNavButton({ onClick, disabled, title, children, longPressDurationMs, onLongPress, rotate = 0 }: PostViewNavButtonProps) {
  const longPressEnabled = !!(longPressDurationMs && onLongPress);
  const [filling, setFilling] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [hintVisible, setHintVisible] = useState(false);
  const [hintNonce, setHintNonce] = useState(0);
  const longPressFiredRef = useRef(false);
  const startTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fireTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!longPressEnabled) {
      setClickCount(0);
      setHintVisible(false);
      if (hintTimerRef.current) { clearTimeout(hintTimerRef.current); hintTimerRef.current = null; }
    }
  }, [longPressEnabled]);

  useEffect(() => {
    if (!longPressEnabled || clickCount == 0 || clickCount % 3 != 0) return;
    setHintVisible(true);
    setHintNonce(n => n + 1);
    if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
    hintTimerRef.current = setTimeout(() => setHintVisible(false), 2500);
  }, [clickCount, longPressEnabled]);

  useEffect(() => () => { if (hintTimerRef.current) clearTimeout(hintTimerRef.current); }, []);

  const clearTimers = useCallback(() => {
    if (startTimerRef.current) { clearTimeout(startTimerRef.current); startTimerRef.current = null; }
    if (fireTimerRef.current) { clearTimeout(fireTimerRef.current); fireTimerRef.current = null; }
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  const handlePointerDown = useCallback(() => {
    if (!longPressEnabled || disabled) return;
    longPressFiredRef.current = false;
    clearTimers();
    setFilling(false);
    startTimerRef.current = setTimeout(() => setFilling(true), longPressDurationMs! / 4);
    fireTimerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      setFilling(false);
      onLongPress!();
    }, longPressDurationMs!);
  }, [longPressEnabled, disabled, longPressDurationMs, onLongPress, clearTimers]);

  const handlePointerEnd = useCallback(() => {
    clearTimers();
    setFilling(false);
  }, [clearTimers]);

  const handleClick = useCallback(() => {
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      return;
    }
    if (longPressEnabled) setClickCount(c => c + 1);
    onClick();
  }, [onClick, longPressEnabled]);

  const fillDuration = longPressEnabled ? longPressDurationMs! - longPressDurationMs! / 4 : 0;

  return (
    <span className="navBtnWrap">
      {hintVisible && <span key={hintNonce} className="navBtnHoldHint">Hold for {title}</span>}
      <button
        className={`postViewNavButton${longPressEnabled ? ' noPressFlash' : ''}`}
        onClick={handleClick}
        onPointerDown={longPressEnabled ? handlePointerDown : undefined}
        onPointerUp={longPressEnabled ? handlePointerEnd : undefined}
        onPointerLeave={longPressEnabled ? handlePointerEnd : undefined}
        onPointerCancel={longPressEnabled ? handlePointerEnd : undefined}
        disabled={disabled}
        tabIndex={-1}
        title={title}
      >
        {longPressEnabled && (
          <span
            className="postViewNavButtonFill"
            style={{
              transform: filling ? 'scaleY(1)' : 'scaleY(0)',
              transition: filling ? `transform ${fillDuration}ms linear` : 'none',
            }}
            aria-hidden="true"
          />
        )}
        <span
          className="postViewNavButtonContent"
          style={{
            transform: `rotate(${rotate}deg)`,
            transition: 'transform 0.25s ease',
          }}
        >{children}</span>
      </button>
    </span>
  );
}
